import { type FormEvent, forwardRef, type RefObject, useEffect, useRef, useState } from 'react';
import { MathfieldElement } from 'mathlive';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useEffectOnce, useOpenWindow } from '@/hooks';
import { QuestionmarkSmallCircleColorIcon } from '@/icons';
import { KeyboardIcon } from '@/icons/components/Keyboard';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogPanel, DialogTitle } from '@/ui';
import { Button, IconButton } from '@/ui_v3';
import { MATHLIVE_FONTS_DIRECTORY } from './mathliveFonts';

// MathLive 기본값 "./fonts" 는 번들 청크 기준으로 풀려 `/_next/static/chunks/fonts/*` 404 가 난다.
// 첫 math-field 가 붙기 전에 정해야 해서 모듈 평가 시점에 설정한다.
if (typeof window !== 'undefined' && MathfieldElement) {
  MathfieldElement.fontsDirectory = MATHLIVE_FONTS_DIRECTORY;
}
const EquationEditorModalComponent = ({
  onSubmit,
  onClose,
  isOpen,
  initialValue
}: {
  onSubmit: (latex: string) => void;
  onClose: () => void;
  isOpen: boolean;
  initialValue?: string;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const mathFieldRef = useRef<MathfieldElement>(null);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const openWindow = useOpenWindow(false);
  const handleLatexInputChange = (e: FormEvent<MathfieldElement>) => {
    const latexValue = e.currentTarget.value;
    if (!latexValue || latexValue.includes('placeholder')) {
      setCanSubmit(false);
    } else setCanSubmit(true);
  };
  const handleInitialValueSet = () => {
    setCanSubmit(true);
  };
  return <Dialog onClick={onClose} onClose={() => {}} open={isOpen} data-sentry-element="Dialog" data-sentry-component="EquationEditorModalComponent" data-sentry-source-file="EquationEditorModal.tsx">
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="EquationEditorModal.tsx" />
      <DialogPanel className={'!w-[90%] !max-w-[520px]'} data-sentry-element="DialogPanel" data-sentry-source-file="EquationEditorModal.tsx">
        <DialogHeader className="!pt-lg" onClose={() => {
        onClose();
        window.mathVirtualKeyboard.hide();
      }} data-sentry-element="DialogHeader" data-sentry-source-file="EquationEditorModal.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="EquationEditorModal.tsx">{t('build:build.equationModal.header')}</DialogTitle>
        </DialogHeader>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="EquationEditorModal.tsx">
          <EquationEditor ref={mathFieldRef} onChange={handleLatexInputChange} initialValue={initialValue} onInitialValueSet={handleInitialValueSet} data-sentry-element="EquationEditor" data-sentry-source-file="EquationEditorModal.tsx" />
        </DialogContent>
        <DialogFooter className="flex justify-between" data-sentry-element="DialogFooter" data-sentry-source-file="EquationEditorModal.tsx">
          <div>
            <Button onClick={() => {
            openWindow(t('build:build.equationModal.guideUrl'));
          }} size="md" leftSlot={<QuestionmarkSmallCircleColorIcon />} intent={'neutral'} variant={'transparent'} data-sentry-element="Button" data-sentry-source-file="EquationEditorModal.tsx">
              {t('build:build.equationModal.guide')}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button className="min-w-[120px]" variant={'contained'} intent={'strong'} disabled={!canSubmit} size={'md'} onClick={e => {
            e.stopPropagation();
            const latex = mathFieldRef.current?.getValue('latex');
            if (latex) {
              onSubmit(`\u200B${latex}\u200B`);
              window.mathVirtualKeyboard.hide();
            }
          }} data-sentry-element="Button" data-sentry-source-file="EquationEditorModal.tsx">
              {t('build:build.equationModal.submit')}
            </Button>
          </div>
        </DialogFooter>
      </DialogPanel>
    </Dialog>;
};
interface EquationEditorProps {
  onChange: (e: FormEvent<MathfieldElement>) => void;
  initialValue?: string;
  onInitialValueSet?: () => void;
}
const EquationEditor = forwardRef<MathfieldElement, EquationEditorProps>((props, ref) => {
  const mathFieldRef = ref as RefObject<MathfieldElement>;
  const handleKeyboardButtonClick = () => {
    const {
      visible
    } = window.mathVirtualKeyboard;
    if (visible) window.mathVirtualKeyboard.hide();else {
      window.mathVirtualKeyboard.show();
      mathFieldRef.current?.focus();
    }
  };
  const configMathField = async () => {
    await window.customElements.whenDefined('math-field');
    MathfieldElement.soundsDirectory = null;
  };
  const configVirtualKeyboard = () => {
    const layoutInfo = window.mathVirtualKeyboard.normalizedLayouts;
    const [firstPage, symbolsPage] = [layoutInfo[0], layoutInfo[1]];
    //기본 symbols 레이아웃엔 ∈ ∪ ∩ ⊂ 는 있는데 공집합 키가 없다.
    //키캡은 고정폭이라 행에 하나를 더 끼우면 잘리므로, 맨 아래 행의 빈
    //[separator] 슬롯(1칸)을 ∅ 키로 채운다 — 기존 키·레이아웃은 그대로.
    const symbolsBottomRow = symbolsPage?.layers[0]?.rows?.at(-1);
    const emptySlot = symbolsBottomRow?.findIndex(keycap => keycap.class === 'separator');
    if (symbolsBottomRow && emptySlot !== undefined && emptySlot >= 0) {
      symbolsBottomRow.splice(emptySlot, 1, {
        latex: '\\emptyset',
        shift: '\\varnothing',
        variants: ['\\varnothing']
      });
    }
    //customizing first row
    const firstPageRows = firstPage?.layers[0]?.rows;
    if (firstPageRows) {
      const [firstRow, secondRow, thirdRow] = [firstPageRows[0], firstPageRows[1], firstPageRows[2]];
      //나누기와 자연로그의 밑에 대한 customizing
      const divide = firstRow.find(keycap => keycap.label === '&divide;');
      if (divide) divide.latex = '\\div';
      const exponentialE = firstRow.find(keycap => keycap.latex === '\\exponentialE');
      if (exponentialE) {
        exponentialE.latex = '\\frac{#@}{#?}';
        exponentialE.shift = '';
      }
      //허수에 대한 customizing
      const imaginary = firstRow.find(keycap => keycap.latex === '\\imaginaryI');
      if (imaginary) {
        imaginary.latex = '#@\\degree';
        imaginary.variants = [];
      }
      //지수 keycap에 대한 customizing
      const exponential = secondRow.find(keycap => keycap.latex === '#@^{#0}}');
      if (exponential) {
        exponential.shift = '#@^{#0}}';
      }
      //곱셈에 대한 customizing
      const multiply = secondRow.find(keycap => keycap.label === '&times;');
      if (multiply) {
        multiply.latex = '\\times';
      }

      //전체 한정자에 대한 customizing
      const forAll = thirdRow.find(keycap => keycap.latex === '\\forall');
      if (forAll) {
        forAll.latex = '#@_{#?}';
        forAll.shift = '#@_{#?}';
      }
    }
  };
  useEffectOnce(() => {
    void configMathField();
    configVirtualKeyboard();
    if (mathFieldRef.current) {
      mathFieldRef.current.defaultMode = 'math';
      mathFieldRef.current.placeholder = '1/2 + 1/4 = 3/4';

      // 초기값이 있으면 설정
      if (props.initialValue && typeof props.initialValue === 'string') {
        // \u200B (Zero Width Space) 제거하고 순수 LaTeX만 추출
        const cleanLatex = props.initialValue.replace(/\u200B/g, '');
        mathFieldRef.current.setValue(cleanLatex);
        // 초기값이 설정되면 부모에게 알림
        props.onInitialValueSet?.();
      }
      mathFieldRef.current.focus();
      window.mathVirtualKeyboard.show();
      mathFieldRef.current.mathVirtualKeyboardPolicy = 'manual';
      mathFieldRef.current.addEventListener('focus', () => {
        window.mathVirtualKeyboard.show();
      });
      mathFieldRef.current.addEventListener('blur', () => {
        window.mathVirtualKeyboard.hide();
      });
    }
  });
  useEffect(() => {
    const mathfield = document.querySelector('math-field');
    const span = mathfield?.shadowRoot?.firstElementChild;
    span?.setAttribute('style', 'width: 100%; height: 100%; display: block; position: relative;');
    const multiplyKey = window.mathVirtualKeyboard.getKeycap(`[*]`);
    if (multiplyKey) {
      multiplyKey.latex = 'x';
    }
  }, []);
  return <div className="mt-[10px] flex w-full items-center">
        <math-field style={{
      width: '100%',
      height: '100%',
      fontSize: '18px',
      padding: '6px'
    }} ref={ref} onInput={props.onChange}></math-field>
        <IconButton onMouseDown={e => {
      e.preventDefault();
      e.stopPropagation();
    }} onClick={handleKeyboardButtonClick} size="sm" intent={'neutral'} variant={'transparent'} className="absolute right-xl">
          <KeyboardIcon />
        </IconButton>
      </div>;
});
EquationEditor.displayName = 'EquationEditor';
export const openEquationEditorModal = ({
  initialValue
}: {
  initialValue?: string;
}) => {
  return overlay.openAsync<string | undefined>(({
    close,
    isOpen
  }) => {
    return <EquationEditorModalComponent isOpen={isOpen} onClose={() => close(undefined)} onSubmit={(latex: string) => {
      close(latex);
    }} initialValue={initialValue} />;
  });
};